import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Users as UsersIcon, Sparkles, Edit2, X, Check, ChevronDown, ChevronUp, Lock, Unlock, FileText, Zap, Trash2, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { api } from '../../api/client';
import { useAppContext } from '../../context/AppContext';

const SectionEditor = ({ section, writingSession, onSectionUpdate }) => {
  const { currentUser } = useAppContext();
  const [content, setContent] = useState(section.content || '');
  const [contributions, setContributions] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [showContributions, setShowContributions] = useState(true);
  const [showAIActions, setShowAIActions] = useState(false);
  const contributionsBottomRef = useRef(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [centerCollapsed, setCenterCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [linkedUncertainty, setLinkedUncertainty] = useState(null);

  // Edit mode states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedTitle, setEditedTitle] = useState(section.title);
  const [editedPrompt, setEditedPrompt] = useState(section.ai_prompt || '');
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);

  // Password protection states
  const [isLocked, setIsLocked] = useState(section.is_password_protected || false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Transcript & takeaways states
  const [transcript, setTranscript] = useState(section.transcript || '');
  const [takeaways, setTakeaways] = useState(section.takeaways || []);
  const [isExtractingTakeaways, setIsExtractingTakeaways] = useState(false);
  const [editingTakeawayIdx, setEditingTakeawayIdx] = useState(null);
  const [editedTakeaway, setEditedTakeaway] = useState('');

  const canEdit = currentUser.role === 'admin' || currentUser.role === 'facilitator';

  const handleDeleteContribution = async (contributionId) => {
    if (!window.confirm('Delete this contribution? This cannot be undone.')) return;
    try {
      await api.deleteContribution(contributionId);
      setContributions(contributions.filter(c => c.id !== contributionId));
    } catch (error) {
      alert('Failed to delete contribution: ' + error.message);
    }
  };
  const canAccess = !isLocked || isUnlocked || canEdit;

  // Auto-scroll contributions to bottom when new ones arrive
  useEffect(() => {
    if (contributionsBottomRef.current) {
      contributionsBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [contributions]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleString();
  };

  const loadContributions = useCallback(async () => {
    if (!section?.id) return;
    try {
      const data = await api.getSectionContributions(section.id);
      setContributions(data);
    } catch (error) {
      console.error('Failed to load contributions:', error);
    }
  }, [section?.id]);

  useEffect(() => {
    setContent(section.content || '');
    setEditedTitle(section.title);
    setEditedPrompt(section.ai_prompt || '');
    setIsEditingTitle(false);
    setIsEditingPrompt(false);
    setIsPromptExpanded(false);
    setIsLocked(section.is_password_protected || false);
    setIsUnlocked(false);
    setPasswordInput('');
    setPasswordError('');
    setTranscript(section.transcript || '');
    setTakeaways(section.takeaways || []);
    loadContributions();
  }, [section.id, loadContributions]);

  useEffect(() => {
    const loadLinkedUncertainty = async () => {
      if (section.uncertainty_id && writingSession.foresight_session_id) {
        try {
          const uncertainties = await api.getUncertainties(writingSession.foresight_session_id);
          const unc = uncertainties.find(u => u.id === section.uncertainty_id);
          setLinkedUncertainty(unc);
        } catch (error) {
          console.error('Failed to load linked uncertainty:', error);
        }
      }
    };
    loadLinkedUncertainty();
  }, [section.uncertainty_id, writingSession.foresight_session_id]);

  const handleUnlock = async () => {
    try {
      const result = await api.verifySectionPassword(section.id, passwordInput);
      if (result.valid) {
        setIsUnlocked(true);
        setPasswordError('');
      } else {
        setPasswordError('Incorrect password. Please try again.');
        setPasswordInput('');
      }
    } catch (e) {
      setPasswordError('Error verifying password.');
    }
  };

  const handleTogglePasswordProtection = async () => {
    if (isLocked) {
      // Require explicit confirmation before removing protection
      const confirmed = window.confirm(
        'Remove password protection from this section?\n\nOnce removed, all participants will be able to access it without a password.'
      );
      if (!confirmed) return;
      await api.updateWritingSection(section.id, { is_password_protected: false, section_password: null });
      onSectionUpdate({ ...section, is_password_protected: false, section_password: null });
      setIsLocked(false);
      setIsUnlocked(false);
    } else {
      setIsEditingPassword(true);
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword.trim()) return;
    await api.updateWritingSection(section.id, { is_password_protected: true, section_password: newPassword });
    onSectionUpdate({ ...section, is_password_protected: true, section_password: newPassword });
    setIsLocked(true);
    setIsUnlocked(true);
    setIsEditingPassword(false);
    setNewPassword('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.updateWritingSection(section.id, { content });
      onSectionUpdate({ ...section, content });
      alert('Saved successfully!');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) { alert('Title cannot be empty'); return; }
    try {
      await api.updateWritingSection(section.id, { title: editedTitle });
      onSectionUpdate({ ...section, title: editedTitle });
      setIsEditingTitle(false);
    } catch (error) { alert('Failed to save title: ' + error.message); }
  };

  const handleSavePrompt = async () => {
    try {
      await api.updateWritingSection(section.id, { ai_prompt: editedPrompt });
      onSectionUpdate({ ...section, ai_prompt: editedPrompt });
      setIsEditingPrompt(false);
      alert('AI prompt updated!');
    } catch (error) { alert('Failed to save prompt: ' + error.message); }
  };

  const handleAddContribution = async () => {
    if (!userInput.trim()) return;
    try {
      const newContribution = await api.addContribution({
        section_id: section.id,
        content: userInput,
        contribution_type: 'text',
      });
      setContributions([...contributions, newContribution]);
      setUserInput('');
    } catch (error) { alert('Failed to add contribution: ' + error.message); }
  };

  const handleSynthesizeContributions = async () => {
    const userContributions = contributions.filter(c => c.contribution_type !== 'ai_generated');
    if (userContributions.length === 0) { alert('No contributions to synthesize.'); return; }
    setIsAIProcessing(true);
    try {
      const contributionsText = userContributions.map(c => `- ${c.content}`).join('\n');
      let response;
      if (section.ai_prompt?.trim()) {
        response = await api.customAIPrompt(section.id, `${section.ai_prompt}\n\nContributions:\n${contributionsText}`);
      } else {
        response = await api.customAIPrompt(section.id, `Synthesize into a cohesive narrative:\n${contributionsText}`);
      }
      setContent(response.response);
      await api.updateWritingSection(section.id, { content: response.response });
      onSectionUpdate({ ...section, content: response.response });
    } catch (error) { alert('Failed to synthesize: ' + error.message); }
    finally { setIsAIProcessing(false); }
  };

  const handleExtractTakeaways = async () => {
    setIsExtractingTakeaways(true);
    try {
      const result = await api.extractTakeaways(section.id, transcript);
      setTakeaways(result.takeaways);
      onSectionUpdate({ ...section, transcript, takeaways: result.takeaways });
    } catch (error) { alert('Failed to extract takeaways: ' + error.message); }
    finally { setIsExtractingTakeaways(false); }
  };

  const handleSaveTranscript = async () => {
    try {
      await api.updateWritingSection(section.id, { transcript });
      onSectionUpdate({ ...section, transcript });
    } catch (error) { alert('Failed to save transcript: ' + error.message); }
  };

  const handleSaveTakeaway = async (idx) => {
    const updated = takeaways.map((t, i) => i === idx ? editedTakeaway : t);
    setTakeaways(updated);
    setEditingTakeawayIdx(null);
    await api.updateWritingSection(section.id, { takeaways: updated });
    onSectionUpdate({ ...section, takeaways: updated });
  };

  const handleDeleteTakeaway = async (idx) => {
    const updated = takeaways.filter((_, i) => i !== idx);
    setTakeaways(updated);
    await api.updateWritingSection(section.id, { takeaways: updated });
    onSectionUpdate({ ...section, takeaways: updated });
  };

  // ---- PASSWORD GATE ------------------------------------------------------------------------------------------------------------------
  if (isLocked && !isUnlocked && !canEdit) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-[#c0c0e6] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#01006A]" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">{section.title}</h3>
          <p className="text-sm text-gray-500 mb-6">This section is password protected.</p>
          <input
            type="password"
            value={passwordInput}
            onChange={e => setPasswordInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleUnlock()}
            placeholder="Enter password"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A] mb-3"
          />
          {passwordError && <p className="text-red-500 text-sm mb-3">{passwordError}</p>}
          <button
            onClick={handleUnlock}
            className="w-full bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] font-semibold"
          >
            Unlock Section
          </button>
        </div>
      </div>
    );
  }

  // ---- MAIN EDITOR ----------------------------------------------------------------------------------------------------------------------
  return (
    <div className="h-full flex">
      {/* Center panel -- collapsible */}
      {centerCollapsed ? (
        /* Collapsed strip */
        <div className="flex flex-col items-center py-4 px-2 bg-white border-r border-gray-200 flex-shrink-0 w-10 gap-4">
          <button
            onClick={() => setCenterCollapsed(false)}
            title="Expand editor"
            className="p-1 text-gray-400 hover:text-[#01006A] rounded transition-colors">
            <PanelLeftOpen className="w-5 h-5" />
          </button>
          {/* Rotated section title */}
          <span className="text-xs font-medium text-gray-400 whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {section.title}
          </span>
        </div>
      ) : (
        /* Expanded center panel */
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Title row */}
              <div className="flex items-center gap-2 mb-1">
                {isEditingTitle ? (
                  <>
                    <input type="text" value={editedTitle} onChange={e => setEditedTitle(e.target.value)}
                      className="text-xl font-bold text-gray-900 border-2 border-[#01006A] rounded px-2 py-1 flex-1" autoFocus />
                    <button onClick={handleSaveTitle} className="p-1 text-[#01006A] hover:bg-[#e6e6f5] rounded"><Check className="w-5 h-5" /></button>
                    <button onClick={() => { setEditedTitle(section.title); setIsEditingTitle(false); }} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-5 h-5" /></button>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-gray-900 truncate">{section.title}</h2>
                    {canEdit && (
                      <button onClick={() => setIsEditingTitle(true)} className="p-1 text-gray-400 hover:text-[#01006A] rounded flex-shrink-0"><Edit2 className="w-4 h-4" /></button>
                    )}
                    {/* Lock indicator */}
                    {isLocked && (
                      <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 flex-shrink-0">
                        <Lock className="w-3 h-3" /> Protected
                      </span>
                    )}
                  </>
                )}
              </div>
              <p className="text-sm text-gray-500 capitalize">{section.section_type.replace('_', ' ')}</p>

              {/* Password control (admin/facilitator only) */}
              {canEdit && (
                <div className="mt-2">
                  {isEditingPassword ? (
                    <div className="flex items-center gap-2">
                      <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        placeholder="Set password" className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]" />
                      <button onClick={handleSetPassword} className="text-xs bg-[#01006A] text-white px-3 py-1 rounded-lg hover:bg-[#010059]">Set</button>
                      <button onClick={() => { setIsEditingPassword(false); setNewPassword(''); }} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={handleTogglePasswordProtection}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${isLocked ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100' : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                      {isLocked ? <><Unlock className="w-3 h-3" /> Remove Password</> : <><Lock className="w-3 h-3" /> Add Password</>}
                    </button>
                  )}
                </div>
              )}

              {/* AI Prompt */}
              <div className="mt-3">
                {isEditingPrompt ? (
                  <div className="border-2 border-[#01006A] rounded-lg p-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Custom AI Prompt:</label>
                    <textarea value={editedPrompt} onChange={e => setEditedPrompt(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm resize-none" rows="5" />
                    <div className="flex gap-2 mt-2">
                      <button onClick={handleSavePrompt} className="flex items-center gap-1 px-3 py-1 bg-[#01006A] text-white text-sm rounded hover:bg-[#010059]"><Check className="w-4 h-4" />Save</button>
                      <button onClick={() => { setEditedPrompt(section.ai_prompt || ''); setIsEditingPrompt(false); }} className="flex items-center gap-1 px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"><X className="w-4 h-4" />Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#e6e6f5] border border-[#9696d4] rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-2">
                      <button onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                        className="flex items-center gap-2 text-xs font-semibold text-[#000030] hover:text-[#010059] flex-1">
                        {isPromptExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {section.ai_prompt ? 'Custom AI Prompt' : 'No custom prompt set'}
                      </button>
                      {canEdit && (
                        <button onClick={() => setIsEditingPrompt(true)} className="flex items-center gap-1 px-2 py-1 text-xs text-[#010059] hover:bg-[#c0c0e6] rounded">
                          <Edit2 className="w-3 h-3" />Edit
                        </button>
                      )}
                    </div>
                    {isPromptExpanded && (
                      <div className="px-2 pb-2 border-t border-[#9696d4] pt-2 max-h-32 overflow-y-auto">
                        <p className="text-xs text-[#010048] whitespace-pre-wrap">{section.ai_prompt || 'None set.'}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {linkedUncertainty && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                  <div className="text-xs font-semibold text-blue-900 mb-1">Linked Uncertainty:</div>
                  <div className="text-sm text-blue-800">{linkedUncertainty.title}</div>
                  {linkedUncertainty.description && <div className="text-xs text-blue-600 mt-1">{linkedUncertainty.description}</div>}
                </div>
              )}
              {!canEdit && (
                <div className="mt-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded inline-block">
                  📝 Main text editing restricted to admins and facilitators
                </div>
              )}
            </div>
            <button onClick={handleSave} disabled={isSaving || !canEdit}
              className="flex items-center space-x-2 bg-[#01006A] text-white px-4 py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-400 ml-4 flex-shrink-0">
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </button>
            {/* Collapse toggle */}
            <button
              onClick={() => setCenterCollapsed(true)}
              title="Collapse editor"
              className="ml-2 p-2 text-gray-400 hover:text-[#01006A] hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
              <PanelLeftClose className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Text areas */}
        <div className="flex-1 p-6 overflow-y-auto flex flex-col gap-4 min-h-0">
          <div className="flex-1 flex flex-col min-h-0">
            <label className="block text-sm font-semibold text-[#000030] mb-2 flex items-center flex-shrink-0">
              <Sparkles className="w-4 h-4 mr-2" />Narrative Synthesis
            </label>
            <textarea value={content} onChange={e => setContent(e.target.value)} disabled={!canEdit}
              className={`flex-1 w-full p-4 border border-[#6b6bc2] rounded-lg focus:ring-2 focus:ring-[#01006A] resize-none min-h-0 ${!canEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              placeholder={canEdit ? "Narrative synthesis appears here..." : "View only"} />
          </div>
        </div>
      </div>
      )} 

      {/* Right Sidebar -- collapsible; also expands to fill when center is collapsed */}
      {rightCollapsed ? (
        /* Collapsed strip */
        <div className="flex flex-col items-center py-4 px-2 bg-gray-50 border-l border-gray-200 flex-shrink-0 w-10 gap-4">
          <button
            onClick={() => setRightCollapsed(false)}
            title="Expand panel"
            className="p-1 text-gray-400 hover:text-[#01006A] rounded transition-colors">
            <PanelRightOpen className="w-5 h-5" />
          </button>
        </div>
      ) : (
      <div className={`${centerCollapsed ? 'flex-1' : 'w-96 flex-shrink-0'} bg-gray-50 border-l border-gray-200 flex flex-col h-full transition-all duration-200`}>
        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 flex flex-shrink-0">
          <button onClick={() => { setShowContributions(true); setShowAIActions(false); setShowTranscript(false); }}
            className={`flex-1 flex items-center justify-center gap-1 py-3 text-xs font-medium ${showContributions ? 'border-b-2 border-[#01006A] text-[#01006A]' : 'text-gray-600'}`}>
            <UsersIcon className="w-4 h-4" />Contributions
          </button>
          <button onClick={() => { setShowContributions(false); setShowAIActions(true); setShowTranscript(false); }}
            className={`flex-1 flex items-center justify-center gap-1 py-3 text-xs font-medium ${showAIActions ? 'border-b-2 border-[#01006A] text-[#01006A]' : 'text-gray-600'}`}>
            <Sparkles className="w-4 h-4" />AI Actions
          </button>
          <button onClick={() => { setShowContributions(false); setShowAIActions(false); setShowTranscript(true); }}
            className={`flex-1 flex items-center justify-center gap-1 py-3 text-xs font-medium ${showTranscript ? 'border-b-2 border-[#01006A] text-[#01006A]' : 'text-gray-600'}`}>
            <FileText className="w-4 h-4" />Transcript
          </button>
          <button
            onClick={() => setRightCollapsed(true)}
            title="Collapse panel"
            className="flex-shrink-0 px-2 flex items-center justify-center text-gray-400 hover:text-[#01006A] transition-colors">
            <PanelRightClose className="w-4 h-4" />
          </button>
        </div>

        {/* Contributions Panel */}
        {showContributions && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {contributions.filter(c => c.contribution_type !== 'ai_generated').length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <UsersIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No contributions yet</p>
                </div>
              )}
              {contributions.filter(c => c.contribution_type !== 'ai_generated').map(contrib => (
                <div key={contrib.id} className="p-3 rounded-lg bg-white border border-gray-200 group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-700">👤 {contrib.user_name || 'User'}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{formatTimestamp(contrib.created_at)}</span>
                      {(contrib.user_id === currentUser.id || currentUser.role === 'admin') && (
                        <button
                          onClick={() => handleDeleteContribution(contrib.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                          title="Delete contribution"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap contribution-text">{contrib.content}</p>
                </div>
              ))}
              <div ref={contributionsBottomRef} />
            </div>
            {canAccess && (
              <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200">
                <textarea value={userInput} onChange={e => setUserInput(e.target.value)}
                  onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddContribution(); }}}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A] resize-none" rows="3"
                  placeholder="Add your contribution..." />
                <button onClick={handleAddContribution} disabled={!userInput.trim()}
                  className="w-full mt-2 bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300">
                  Add Contribution
                </button>
              </div>
            )}
          </div>
        )}

        {/* AI Actions Panel */}
        {showAIActions && (
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto p-4">
            {canEdit ? (
              <div className="space-y-4">
                <div className="bg-[#e6e6f5] border border-[#9696d4] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-[#01006A]" />
                    <h3 className="text-sm font-semibold text-[#000030]">AI-Powered Analysis</h3>
                  </div>
                  <p className="text-xs text-[#010059] mb-4">Analyze and synthesize team contributions.</p>
                  <div className="space-y-3">
                    <button onClick={handleSynthesizeContributions} disabled={isAIProcessing}
                      className="w-full flex items-center justify-center gap-2 bg-[#01006A] text-white py-3 rounded-lg hover:bg-[#010059] disabled:bg-gray-400">
                      <Sparkles className="w-4 h-4" />
                      {isAIProcessing ? 'Processing...' : 'Synthesize All Contributions'}
                    </button>
                    <p className="text-xs text-gray-600">Synthesizes contributions into a cohesive narrative.</p>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">💡 <strong>Tip:</strong> Add all contributions first, then use AI to analyze them.</p>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-sm font-medium mb-2">AI Actions Restricted</p>
                <p className="text-xs">Only admins and facilitators can trigger AI synthesis.</p>
              </div>
            )}
          </div>
        )}

        {/* Transcript & Takeaways Panel */}
        {showTranscript && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">

              {/* Source summary */}
              <div className="bg-[#e6e6f5] border border-[#9696d4] rounded-lg px-3 py-2 text-xs text-[#010048]">
                <span className="font-semibold">Analysis will include: </span>
                <span className={contributions.filter(c => c.contribution_type !== 'ai_generated').length > 0 ? 'text-[#010059]' : 'text-gray-400'}>
                  {contributions.filter(c => c.contribution_type !== 'ai_generated').length} contribution{contributions.filter(c => c.contribution_type !== 'ai_generated').length !== 1 ? 's' : ''}
                </span>
                {transcript.trim() && <span className="text-[#010059]"> + transcript</span>}
              </div>

              {/* Transcript input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4" />Meeting Transcript
                  <span className="text-xs font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={transcript}
                  onChange={e => setTranscript(e.target.value)}
                  className="w-full h-36 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A] resize-none"
                  placeholder="Paste a meeting transcript here, or leave blank to analyze contributions only…"
                />
                <div className="flex gap-2 mt-2">
                  <button onClick={handleSaveTranscript} disabled={!transcript.trim()}
                    className="flex-1 flex items-center justify-center gap-1 text-xs bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-40">
                    <Save className="w-3 h-3" />Save Transcript
                  </button>
                  <button onClick={handleExtractTakeaways}
                    disabled={isExtractingTakeaways || (contributions.filter(c => c.contribution_type !== 'ai_generated').length === 0 && !transcript.trim())}
                    className="flex-1 flex items-center justify-center gap-1 text-xs bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-400">
                    <Zap className="w-3 h-3" />
                    {isExtractingTakeaways ? 'Extracting...' : 'Extract Takeaways'}
                  </button>
                </div>
              </div>

              {/* Takeaways list */}
              {takeaways.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />Key Takeaways
                      <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{takeaways.length}</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    {takeaways.map((t, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <span className="text-xs font-bold text-amber-600 mt-0.5 flex-shrink-0">{idx + 1}</span>
                        {editingTakeawayIdx === idx ? (
                          <div className="flex-1">
                            <textarea value={editedTakeaway} onChange={e => setEditedTakeaway(e.target.value)}
                              className="w-full text-xs border border-amber-300 rounded p-1 resize-none" rows="3" autoFocus />
                            <div className="flex gap-1 mt-1">
                              <button onClick={() => handleSaveTakeaway(idx)} className="text-xs text-[#01006A] hover:text-[#010059] font-medium">Save</button>
                              <button onClick={() => setEditingTakeawayIdx(null)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-amber-900 flex-1 takeaway-text">{t}</p>
                            <div className="flex gap-1 flex-shrink-0">
                              <button onClick={() => { setEditingTakeawayIdx(idx); setEditedTakeaway(t); }}
                                className="p-1 text-amber-500 hover:text-amber-700 rounded"><Edit2 className="w-3 h-3" /></button>
                              <button onClick={() => handleDeleteTakeaway(idx)}
                                className="p-1 text-red-400 hover:text-red-600 rounded"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {takeaways.length === 0 && (contributions.filter(c => c.contribution_type !== 'ai_generated').length > 0 || transcript.trim()) && (
                <div className="text-center text-gray-400 py-4">
                  <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Click "Extract Takeaways" to analyze.</p>
                </div>
              )}

              {takeaways.length === 0 && contributions.filter(c => c.contribution_type !== 'ai_generated').length === 0 && !transcript.trim() && (
                <div className="text-center text-gray-400 py-4">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Add contributions in the Contributions tab, or paste a transcript above, then click Extract Takeaways.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default SectionEditor;
